import * as React from 'react';
import type { TAllowedColors } from '../tag/types';
import { Body2Typography } from '../typography';
import { cn } from '@utils';

/**
 * Color map for StatusIndicator bullet colors.
 * Light, vibrant colors for status indication.
 */
export const StatusIndicatorColorMap: Record<TAllowedColors, string> = {
	green: '#24C875',
	blue: '#4A90E2',
	orange: '#FA771E',
	gray: '#9B9B9B',
	yellow: '#F5C842',
	red: '#E85757',
	purple: '#9B6DD6',
	darkGray: '#6B6B6B',
	tertiary: '#8D9CAD',
	pink: '#E878A0',
	teal: '#26B8B8',
	navy: '#4A6FA5',
	'golden-brown': '#C4964A',
};

export type StatusIndicatorProps = {
	/** The label text to display */
	label: string;
	/** The color theme */
	color: TAllowedColors;
	/** Optional className for the text */
	textClassName?: string;
} & React.HTMLAttributes<HTMLDivElement>;

/**
 * StatusIndicator component displays a colored bullet with status text.
 *
 * @example
 * <StatusIndicator label="Published" color="green" />
 * <StatusIndicator label="Draft" color="teal" />
 */
export const StatusIndicator = React.forwardRef<HTMLDivElement, StatusIndicatorProps>(
	({ label, color, textClassName, className, ...props }, ref) => {
		const bulletColor = StatusIndicatorColorMap[color] || StatusIndicatorColorMap.gray;

		return (
			<div ref={ref} className={cn('flex items-center gap-1.5', className)} data-aid="status-indicator" {...props}>
				<div className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: bulletColor }} />
				<Body2Typography className={cn('text-secondary', textClassName)}>{label}</Body2Typography>
			</div>
		);
	}
);

StatusIndicator.displayName = 'StatusIndicator';
